import { useState, useEffect } from 'react';
import { getToken, useToken } from '../utils/token';
import { hasFeature, hasAnyFeature, hasAllFeatures, getAllFeatures, isAppToken } from '../utils/auth';

/**
 * React hook for checking user permissions based on feature flags
 *
 * @example
 * ```typescript
 * const { canImpersonate, features } = useAuth();
 *
 * if (canImpersonate) {
 *   // Show impersonate button
 * }
 * ```
 */
export function useAuth() {
  const { token: tokenFromHook } = useToken();
  const [token, setToken] = useState<string>('');
  const [features, setFeatures] = useState<string[]>([]);

  useEffect(() => {
    const currentToken = tokenFromHook ?? getToken();
    setToken(currentToken ?? '');
    setFeatures(getAllFeatures(currentToken ?? ''));
  }, [tokenFromHook]);

  return {
    /**
     * Check if user has a specific feature
     */
    hasFeature: (featureName: string) => hasFeature(featureName, token),

    /**
     * Check if user has any of the specified features
     */
    hasAnyFeature: (featureNames: string[]) => hasAnyFeature(featureNames, token),

    /**
     * Check if user has all of the specified features
     */
    hasAllFeatures: (featureNames: string[]) => hasAllFeatures(featureNames, token),

    /**
     * Check if the current token is an app token (not a user token)
     */
    isAppToken: () => isAppToken(token),

    /**
     * All features available to the current user
     */
    features,

    /**
     * Convenience flags for common features
     */
    canImpersonate: hasFeature('impersonate_users', token),
    canResetPassword: hasFeature('reset_user_password', token),
    canReconcileUsers: hasFeature('reconcile_users', token),
    canEditRoles: hasFeature('admin_create_edit_role', token),
    canRemoveMultipleUsers: hasFeature('remove_multiple_users', token),
  };
}
